import { useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Chapter, Reveal } from "./Shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-400/20";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", shop: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        shop: form.shop.trim(),
        message: form.message.trim(),
      });
      if (!data?.ok) throw new Error("unexpected response");
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your message — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative">
      <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
        <Reveal className="text-center">
          <Chapter num="09" label="Talk to Us" className="justify-center" />
          <h2 className="mt-6 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
            Have questions before you start?
          </h2>
        </Reveal>

        <div className="mt-12 grid items-start gap-8 lg:grid-cols-[1fr_1.2fr]">
          <Reveal delay={0.08}>
            <p
              data-testid="contact-side-text"
              className="max-w-sm text-base leading-relaxed text-slate-300 lg:mt-4"
            >
              Our studio team replies within a few hours. No bots, no scripts — a real person from the
              Grapho team.
            </p>
          </Reveal>

          <Reveal delay={0.16}>
            {sent ? (
              <div
                data-testid="contact-success"
                className="glass rounded-3xl p-8 text-center"
              >
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
                <p className="mt-4 text-lg font-bold text-white">
                  Message sent. One of our designers will reply within a few hours.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form" className="glass space-y-4 rounded-3xl p-7 sm:p-8">
                <div className="grid gap-4 sm:grid-cols-2">
                  <input
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Full Name"
                    className={inputCls}
                  />
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="Email Address"
                    className={inputCls}
                  />
                </div>
                <input
                  data-testid="contact-input-shop"
                  type="text"
                  value={form.shop}
                  onChange={set("shop")}
                  placeholder="Etsy Shop URL or Name (optional)"
                  className={inputCls}
                />
                <textarea
                  data-testid="contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us about your shop and what you need."
                  className="w-full resize-none rounded-xl border border-white/10 bg-[#070B14]/80 px-4 py-3.5 text-sm leading-relaxed text-slate-100 placeholder:text-slate-600 focus:border-amber-400/60 focus:outline-none focus:ring-2 focus:ring-amber-400/20"
                />
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-amber-400 px-8 py-4 text-base font-bold text-[#1A1204] transition-all duration-300 hover:bg-amber-300 hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                  {sending ? "Sending..." : "Send to Our Team"}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}